import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ApiService } from "@app/core/services";
import { LanguageService } from "@app/shared/services/language.service";
import { environment } from "@env/environment";
import { jwtDecode } from "jwt-decode";

@Injectable({
  providedIn: "root",
})
export class TokenService {
  private http = inject(HttpClient);
  private apiService = inject(ApiService);

  constructor(private _langService: LanguageService) { }

  /**
   * Retrieves the value of a cookie by its name.
   *
   * @param name - The name of the cookie to retrieve.
   * @returns The value of the cookie if found, or `null` if the cookie does not exist.
   */
  getCookie(name: string): string | null {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.startsWith(nameEQ)) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }
    return null;
  }

  /**
   * Obtiene el token almacenado en las cookies.
   * @returns El token o null si no está definido.
   */
  getToken(): string | null {
    const token = this.getCookie("accessToken");
    return token;
  }



  /**
* Limpia las cookies de sesión si existen.
* Retorna una Promise que resuelve con un mensaje.
*/
  clearSessionCookies(): Promise<string> {
    return new Promise((resolve) => {
      const cookiesRemoved: string[] = [];
      const accessToken = this.getCookie('accessToken');
      const refreshToken = this.getCookie('refreshToken');

      // Verifica si existen las cookies y las elimina
      if (accessToken) {
        this.clearCookie('accessToken');
        cookiesRemoved.push('accessToken');
      }

      if (refreshToken) {
        this.clearCookie('refreshToken');
        cookiesRemoved.push('refreshToken');
      }

      // Responde según si se eliminaron cookies o no
      if (cookiesRemoved?.length > 0) {
        resolve(`Cookies eliminadas: ${cookiesRemoved.join(', ')}`);
      } else {
        resolve('No había cookies para eliminar, continuar con la ejecución');
      }
    });
  }



  /**
   * Elimina el token de las cookies.
   */
  clearCookie(name: string, domain?: string): void {
    const date = new Date();
    date.setTime(date.getTime() + -1 * 24 * 60 * 60 * 1000); //
    this.saveToken(name, "", date); // Establece el tiempo de expiración en el pasado
  }

  /**
   * Verifica si el token es válido comprobando su formato o expiración.
   * @returns si el token es válido y el payload del token decode
   */
  isAuthenticated(): { isAuthenticated: boolean; payload?: any } {
    const token = this.getToken();
    if (!token) {
      return { isAuthenticated: false };
    }

    const { isValid, payload } = this.decodeToken(token);
    if (!isValid || !payload || !payload.exp) {
      return { isAuthenticated: false };
    }
    const currentTime = Math.floor(Date.now() / 1000);
    const isValidToken = payload.exp > currentTime; //comparación de fechas en formato UNIX
    return { isAuthenticated: isValidToken, payload };
  }

  /**
   * Decodifica el token JWT para extraer su payload.
   * @param token El token a decodificar
   * @returns El payload del token o null si el formato no es válido.
   */

  decodeToken(token: string): any {
    try {
      const decodedToken: any = jwtDecode(token);
      return { isValid: true, payload: decodedToken };
    } catch (error) {
      this.apiService.handleError<any>("decodeToken", [], error);
      return { isValid: false };
    }
  }

  /**
   * Verifica si el token es válido comprobando su formato o expiración.
   * @returns true si el token es válido, false en caso contrario.
   */
  isTokenValid(): boolean {
    const token = this.getToken();

    if (!token) {
      return false;
    }

    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      return false;
    }

    //Si el token ya venció es inválido
    const currentTime = Math.floor(Date.now() / 1000); // Tiempo actual en segundos
    return payload.exp > currentTime;
  }

  getCliendIdParam() {
    const { isAuthenticated, payload } = this.isAuthenticated();
    if (!isAuthenticated) return;
    if (payload?.fuente != "form") {
      //Si viene por fuente externa (Google, Facebook, Apple)
      return "broker-users";
    }
    //Si viene por fuente = formulario
    return "users";
  }

  /**
   * comprueba que existan tokens en las cookies, y si es así refresca los token,
   * Se va a ejecutar siempre que cargue la app
   */
  refreshSession(): Promise<void> {
    const token = this.getCookie("accessToken");
    const refreshToken = this.getCookie("refreshToken");

    if (token && refreshToken) {
      this.refreshToken(refreshToken);
    }
    return Promise.resolve(); // No hay tokens para restaurar
  }

  /**
   * Refresca token cada que sea necesario
   * @returns El access_token actualizado
   */
  refreshToken(refreshToken): Promise<void> {
    const params = new HttpParams()
      .set("client_id", this.getCliendIdParam())
      .set("grant_type", "refresh_token")
      .set("refresh_token", refreshToken)
      .toString();

    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    return new Promise((resolve, reject) => {
      // Implementa la lógica para refrescar el token desde el backend
      const endPoint =
        "https://kyclk.edutin.com/realms/edutin/protocol/openid-connect/token";
      this.http.post(endPoint, params, { headers }).subscribe({
        next: (refreshResponse: any) => {
          this.saveNewToken(refreshResponse);
          resolve();
        },
        error: (err) => reject(err),
      });
    });
  }

  saveNewToken(refreshResponse: any) {
    // Almacena los token obtenidos al refrescar la sesión en las cookies
    const { access_token, expires_in, refresh_expires_in, refresh_token } =
      refreshResponse;
    const now = new Date();
    const accessTokenExp = new Date(now.getTime() + expires_in * 1000);
    const refreshTokenExp = new Date(now.getTime() + refresh_expires_in * 1000);
    this.saveToken("accessToken", access_token, accessTokenExp);
    this.saveToken("refreshToken", refresh_token, refreshTokenExp);
  }

  /**
   * Guarda el token en las cookies.
   * @param name nombre de la cookie a guardar
   * @param value valor de la cookie a guardar
   * @param days duración en dias de la cookie
   * @param domain dominio de las cookies
   */
  saveToken(name: string, value: string, date: Date): void {
    const appDomain = this.getDomain();
    const expires = `expires=${date.toUTCString()}`;
    const secureFlag = window.location.protocol === "https:" ? "; Secure" : "";
    try {
      document.cookie = `${name}=${value}; ${expires}; path=/; domain=${appDomain}${secureFlag}; SameSite=Strict`;
    } catch (error) {
      console.log(`%c * error *`, "color:red", error);
    }
  }

  /**
   * Retrieves the domain based on the application's environment configuration.
   *
   * @returns {string} The domain string. If the domain includes '127.0.0.1',
   * it returns '127.0.0.1'. If it includes 'dev-app-local.com', it returns
   * '.dev-app-local.com'. Otherwise, it defaults to '.edutin.com'.
   */
  getDomain() {
    const domain: string = environment.appDomain;
    if (domain.includes("127.0.0.1")) {
      return "127.0.0.1"; // Corriendo en localhost
    }
    if (domain.includes("dev-app-local.com")) {
      return ".dev-app-local.com"; // Corriendo local con ssl
    }
    return ".edutin.com"; // Publicado en dev o prod
  }

  generateRandomString() {
    var text = "";
    var possible =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (var i = 0; i < 12; i++)
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    return text;
  }

  /**
   * crea un objeto que contiene información sobre la fuente de la solicitud, una URL de retorno y un nonce para seguridad, lo convierte en una cadena JSON y luego lo codifica en Base64 antes de devolverlo.
   */
  getState(path: string = ""): string {
    const domain = window.location.hostname;
    if (domain.includes('127.0.0.1')) {
      return btoa(JSON.stringify({
        source: 'localhost',
        returnUrl: window.location.href,// retorna nuevamente a la URL actual después de la autenticación
        nonce: this.generateRandomString()
      }));
    }

    return btoa(
      JSON.stringify({
        source: "affiliate", // app || business || assist || designer || affiliate
        returnUrl: environment.appDomain + path,
        nonce: this.generateRandomString(),
      }),
    );
  }
}
